import type { SearchEntryType, SearchGroup, SearchIndexEntry } from "@/hooks/useSearch";

/**
 * 種類の並び (/luagate の中の窓が使う既定)。学習パス、実践プロジェクト、テキスト、授業アーカイブ、CS、用語。
 * 学ぶ道の本線 (学習パスと実践) を先に、読む物と見る物 (テキスト、授業) を次に、引く物 (CS と用語) を最後に置く。
 * サイドバーの並び (学習パス、授業アーカイブ、テキスト) のうち、授業とテキストだけは入れ替えた (読んで調べる物を、見て調べる物より先に)。
 * CS (/computer-science) はログイン無しで開ける公開の頁なので、会員の個人の進み (学習パス) より後ろ、引く物の用語の手前に置く。
 * 探せるものの札と、1 列でない窓のまとまりの順がこれを読む
 */
export const SEARCH_GROUP_ORDER: SearchEntryType[] = ["lesson", "chapter", "textbook", "video", "cs", "glossary"];

/**
 * 公開の頁 (帯の虫めがね、GlobalSearch) が使う並び。ログインしていない人には会員向けの種類 (学習パス・実践・テキスト・
 * 授業アーカイブ) は口 (api.search) がそもそも返さないので、ここでの並びが効くのは会員がログインしたまま公開の頁で
 * 探したとき。公開の頁を見ている最中は、自分の学習パスへ飛ばすより、誰でも開ける CS と用語集を先に見せる
 */
export const PUBLIC_SEARCH_GROUP_ORDER: SearchEntryType[] = ["cs", "glossary", "textbook", "video", "chapter", "lesson"];

/** 口の返事 (種類ごとのまとまり) を、表示の順に並べ、空のまとまりを省く */
export function orderSearchGroups(groups: SearchGroup[], order: readonly SearchEntryType[] = SEARCH_GROUP_ORDER): SearchGroup[] {
  const byType = new Map(groups.map((g) => [g.type, g] as const));
  return order.map((type) => byType.get(type)).filter((g): g is SearchGroup => !!g && g.items.length > 0);
}

/** キーボードで選ぶための、まとまりをならした 1 本の並び */
export function flattenSearchGroups(
  groups: SearchGroup[],
  options: { byRank?: boolean; order?: readonly SearchEntryType[] } = {},
): SearchIndexEntry[] {
  // 口の返事の行には種類が入らない (まとまりが持つ)。行の頭の札が種類を出せるよう、ここで行に写す
  const items = orderSearchGroups(groups, options.order).flatMap((g) => g.items.map((item) => (item.type ? item : { ...item, type: g.type })));
  // 1 列の窓は、種類のまとまりの順でなく口が付けた関連の順 (題に当たる物が先) で並べる。順位の無い返事はまとまりの順のまま
  if (options.byRank && items.every((item) => typeof item.rank === "number")) {
    return [...items].sort((a, b) => (a.rank as number) - (b.rank as number));
  }
  return items;
}

/** 試しの人が有料の中身を選んだときは、行き先に ?locked=1 を付ける */
export function searchDestination(item: SearchIndexEntry, isTrial: boolean): string {
  if (!item.url) return "";
  if (isTrial && item.isTrialVisible === false) {
    return `${item.url}${item.url.includes("?") ? "&" : "?"}locked=1`;
  }
  return item.url;
}
